import { NavLink } from 'react-router';

import { DominoMark } from '@/components/common/domino-mark';
import { useAuth } from '@/features/auth/auth-provider';
import { cn } from '@/lib/utils';

import { NAV_GROUPS, type NavItem } from './navigation';

function NavRow({ item }: { item: NavItem }) {
  const Icon = item.icon;

  if (item.planned) {
    return (
      <span
        aria-disabled="true"
        title="Not built yet"
        className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-foreground/35"
      >
        <Icon className="size-4 shrink-0" aria-hidden="true" />
        <span className="truncate">{item.label}</span>
        <span className="ml-auto rounded border border-sidebar-border px-1 text-[10px] tracking-wide uppercase">
          soon
        </span>
      </span>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
          'focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:outline-none',
          isActive
            ? 'bg-sidebar-primary font-medium text-sidebar-primary-foreground'
            : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        )
      }
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

export function Sidebar() {
  const { canAny } = useAuth();

  // An empty `permissions` array means the item is open to any signed-in user.
  // A group whose every item is hidden disappears with its heading, rather than
  // leaving a label above nothing.
  const groups = NAV_GROUPS.map((group) => ({
    label: group.label,
    items: group.items.filter((item) => item.permissions.length === 0 || canAny(item.permissions)),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-sidebar text-sidebar-foreground">
      <div className="flex h-16 shrink-0 items-center gap-2.5 border-b border-sidebar-border px-5">
        <DominoMark className="size-6 shrink-0" />
        <span className="truncate font-display text-lg">Domino Peak</span>
      </div>

      <nav aria-label="Main" className="min-h-0 flex-1 space-y-6 overflow-y-auto px-3 py-5">
        {groups.map((group) => (
          <div key={group.label}>
            <p className="px-3 pb-1.5 text-[11px] font-semibold tracking-[0.14em] text-sidebar-foreground/45 uppercase">
              {group.label}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavRow item={item} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </div>
  );
}
